import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';

interface PhotoFeedbackScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  feedbackSessionId?: string;
}

export default function PhotoFeedbackScreen({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
  feedbackSessionId,
}: PhotoFeedbackScreenProps) {
  const {
    backendBaseUrl,
    product,
    style,
    userEmail,
    sessionId,
    cycleCustomerId,
    setCycleCustomerId,
    consents,
    deviceData,
  } = useAppContext();

  const effectiveSessionId = feedbackSessionId || sessionId;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbScrollRef = useRef<HTMLDivElement>(null);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [text] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateThumbScrollState = () => {
    const el = thumbScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    updateThumbScrollState();
  }, [previews]);

  const scrollThumbs = (direction: 'left' | 'right') => {
    const el = thumbScrollRef.current;
    if (!el) return;
    const amount = 98; // thumbnail width (90px) + gap (8px)
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setImages(prev => [...prev, ...files]);
    setPreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);

    e.target.value = '';
  };

  const removeImage = (index: number) => {
    URL.revokeObjectURL(previews[index]);
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (!images.length || sending) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('product_id', String(product?.id));
      formData.append('text', text.trim());
      formData.append('session_id', effectiveSessionId || '');
      formData.append('customer_id', cycleCustomerId || '');
      formData.append('userEmail', userEmail || '');
      formData.append('question', product?.product_indication || '');
      formData.append('consents', JSON.stringify(consents || []));
      formData.append('cycle_count', deviceData.cycle_count || '');
      formData.append('codentify', deviceData.codentify || '');
      formData.append('purchase_date', deviceData.purchase_date || '');
      formData.append('adc', deviceData.adc || '');
      formData.append('months', deviceData.months || '');
      images.forEach(img => formData.append('images', img));

      const response = await axios.post(
        `${backendBaseUrl}/send-feedback-with-photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.customer_id) {
        setCycleCustomerId(response.data.customer_id);
      }
    } catch (error: any) {
      // Si prosegue comunque (l'utente non deve restare bloccato), ma senza status
      // e body un rifiuto — es. il 422 del gate consensi — era indistinguibile da
      // un salvataggio andato a buon fine.
      console.error(
        `Failed to send photo feedback: HTTP ${error?.response?.status ?? '?'}`,
        error?.response?.data ?? error?.message,
        {
          product: product?.code,
          customer_id: cycleCustomerId,
          consents,
        }
      );
    } finally {
      setSending(false);
      setSent(true);
      onNext();
    }
  };

  const buttonBg =
    style?.text_review_button_bg_color ||
    'linear-gradient(to right, #6991F8 0%, #07296a 100%)';
  const buttonColor = style?.text_review_button_text_color || 'white';

  const captureBg = style?.photo_feedback_capture_button_bg_color || buttonBg;
  const captureColor =
    style?.photo_feedback_capture_button_text_color || buttonColor;
  const captureShadow =
    '0 5px 5px ' +
    (style?.photo_feedback_capture_button_shadow_color ||
      style?.text_review_button_shadow_color ||
      '#FFFFFF');

  const sendBg = style?.photo_feedback_send_button_bg_color || buttonBg;
  const sendColor = style?.photo_feedback_send_button_text_color || buttonColor;
  const sendShadow =
    '0 5px 5px ' +
    (style?.photo_feedback_send_button_shadow_color ||
      style?.text_review_button_shadow_color ||
      '#FFFFFF');

  return (
    <div
      style={{
        margin: '0 auto',
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <style>{'.pfs-thumb-scroll::-webkit-scrollbar { display: none; }'}</style>
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingBottom: '8px',
          gap: 'clamp(12px, 2vh, 20px)',
          marginTop: '1.6vh',
        }}
      >
        {/* Hidden file input: opens camera on mobile, file picker on desktop */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          style={{ display: 'none' }}
          onChange={handleCapture}
        />

        {/* Camera button — hidden after send */}
        {!sent && (
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              height: '4.6vh',
              minHeight: '36px',
              width: 'min(34vh, 90vw)',
              maxWidth: '420px',
              borderRadius: '7px',
              padding: '0 20px',
              fontSize: '90%',
              fontWeight: 'bold',
              color: captureColor,
              background: captureBg,
              border: 'none',
              cursor: 'pointer',
              boxShadow: captureShadow,
            }}
          >
            {style?.photo_feedback_capture_indication || 'Scatta una foto'}
          </button>
        )}

        {/* Image previews: single scrollable row with side arrows (avoids pushing the send button off-screen on mobile) */}
        {previews.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              width: 'min(34vh, 90vw)',
              maxWidth: '420px',
            }}
          >
            <button
              type="button"
              onClick={() => scrollThumbs('left')}
              disabled={!canScrollLeft}
              aria-label="Foto precedenti"
              style={{
                flex: '0 0 auto',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: '14px',
                lineHeight: '24px',
                textAlign: 'center',
                padding: 0,
                cursor: canScrollLeft ? 'pointer' : 'default',
                opacity: canScrollLeft ? 1 : 0,
                visibility: canScrollLeft ? 'visible' : 'hidden',
                transition: 'opacity 0.15s',
              }}
            >
              ‹
            </button>

            <div
              ref={thumbScrollRef}
              onScroll={updateThumbScrollState}
              className="pfs-thumb-scroll"
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '8px',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              {previews.map((src, i) => (
                <div
                  key={i}
                  style={{ position: 'relative', flex: '0 0 auto' }}
                >
                  <img
                    src={src}
                    alt={`foto ${i + 1}`}
                    style={{
                      width: '90px',
                      height: '90px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '1px solid #ccc',
                    }}
                  />
                  <button
                    onClick={() => removeImage(i)}
                    style={{
                      position: 'absolute',
                      top: '3px',
                      right: '3px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.55)',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      lineHeight: '20px',
                      textAlign: 'center',
                      padding: 0,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => scrollThumbs('right')}
              disabled={!canScrollRight}
              aria-label="Foto successive"
              style={{
                flex: '0 0 auto',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: 'none',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: '14px',
                lineHeight: '24px',
                textAlign: 'center',
                padding: 0,
                cursor: canScrollRight ? 'pointer' : 'default',
                opacity: canScrollRight ? 1 : 0,
                visibility: canScrollRight ? 'visible' : 'hidden',
                transition: 'opacity 0.15s',
              }}
            >
              ›
            </button>
          </div>
        )}

        {/* Send button — shown only when at least one photo is loaded, not sending, and not yet sent */}
        {!sent && !sending && images.length > 0 && (
          <button
            onClick={handleSend}
            style={{
              height: '4.6vh',
              minHeight: '36px',
              width: 'min(34vh, 90vw)',
              maxWidth: '420px',
              borderRadius: '7px',
              padding: '0 20px',
              fontSize: '90%',
              fontWeight: 'bold',
              color: sendColor,
              background: sendBg,
              border: 'none',
              cursor: 'pointer',
              boxShadow: sendShadow,
            }}
          >
            {style?.photo_feedback_send_indication || 'Invia foto'}
          </button>
        )}

        {/* Back */}
        {showBackButton && (
          <button
            type="button"
            onClick={() => onBack?.()}
            style={{
              fontSize: '18px',
              textDecoration: 'underline',
              color: style?.back_button_text_color || '#07296A',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: style?.back_button_indication || 'Back',
              }}
            />
          </button>
        )}

        {/* Skip */}
        {showSkipNextScreenButton && (
          <button
            type="button"
            onClick={onNext}
            style={{
              fontSize: '18px',
              textDecoration: 'underline',
              color: style?.skip_next_screen_button_text_color || '#07296A',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: style?.skip_next_screen_button_indication || 'Salta',
              }}
            />
          </button>
        )}
      </div>
    </div>
  );
}
